import {
	add,
	addDays,
	eachDayOfInterval,
	endOfMonth,
	format,
	getDay,
	getMonth,
	isAfter,
	isBefore,
	isSameDay,
	isWithinInterval,
	startOfMonth,
	startOfToday,
	subDays,
} from 'date-fns'
import { useCallback, useEffect, useState } from 'react'

import SvgIcon from '@/components/svg-icon/svgIcon'
import { Typography } from '@/components/typography/typography'
import { fontStyles } from '@/components/typography/typographyUtils'
import { cn } from '@/libs/tw'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { formatCurrency } from '@/utils/formatCurrency'
import { translate } from '@/utils/translator'

import { useCalendarDaysForPeriod } from '../hooks/useCalendarDaysForPeriod'
import { useHolidays } from '../hooks/useHolidays'
import { MappedDayInfo } from '../types'
import { MinNightsPopup } from './minNightsPopup'

const colStartClasses = [
	'col-start-7',
	'',
	'col-start-2',
	'col-start-3',
	'col-start-4',
	'col-start-5',
	'col-start-6',
]

const i18nMonths = [
	'travel-period-months-january',
	'travel-period-months-february',
	'travel-period-months-march',
	'travel-period-months-april',
	'travel-period-months-may',
	'travel-period-months-june',
	'travel-period-months-july',
	'travel-period-months-august',
	'travel-period-months-september',
	'travel-period-months-october',
	'travel-period-months-november',
	'travel-period-months-december',
] as const

type TravelPeriodCalendarProps = {
	setIsMinNightValid: (value: boolean) => void
}

export const TravelPeriodCalendar = ({ setIsMinNightValid }: TravelPeriodCalendarProps) => {
	const setTravelDate = useCartStore.use.setTravelDate()
	const setShowCrossSelling = useCartStore.use.setShowCrossSelling()
	const { startDate, endDate, nights } = useCartStore.use.travelPeriod()
	const { data: holidays } = useHolidays()
	const [earliestDeparture, setEarliestDeparture] = useState<{
		date: Date
		earliestDeparture: Date
		minNights: number
	}>()

	const today = startOfToday()

	const [startOfCurrentMonth, setStartOfCurrentMonth] = useState(startOfMonth(startDate ?? today))
	const endOfCurrentMonth = endOfMonth(startOfCurrentMonth)

	const startOfNextMonth = add(startOfCurrentMonth, { months: 1 })
	const endOfNextMonth = endOfMonth(startOfNextMonth)

	const { data: infoForDays } = useCalendarDaysForPeriod(startOfCurrentMonth, endOfNextMonth)

	const daysOfFirstMonth = eachDayOfInterval({
		start: startOfCurrentMonth,
		end: endOfCurrentMonth,
	})

	const daysOfSecondMonth = eachDayOfInterval({
		start: startOfNextMonth,
		end: endOfNextMonth,
	})

	const setPreviousMonth = () => setStartOfCurrentMonth(add(startOfCurrentMonth, { months: -1 }))
	const setNextMonth = () => setStartOfCurrentMonth(startOfNextMonth)

	const [hoverDay, setHoverDay] = useState<Date | undefined>(undefined)
	const isHover = (day: Date) => {
		if (startDate && endDate) return

		return (
			hoverDay &&
			startDate &&
			!endDate &&
			isWithinInterval(day, {
				start: isAfter(hoverDay, startDate) ? startDate : hoverDay,
				end: isAfter(hoverDay, startDate) ? hoverDay : startDate,
			})
		)
	}

	const hasHoliday = (day: Date) => {
		return {
			publicHolidays: Boolean(
				holidays?.publicHolidays?.find((holiday) => isSameDay(holiday.date, day))
			),
			schoolHolidays: Boolean(
				holidays?.schoolHolidays?.find((holiday) => isSameDay(holiday.date, day))
			),
		}
	}

	const getDayInfo = useCallback(
		(day: Date) => infoForDays?.find((info) => isSameDay(info.date, day)),
		[infoForDays]
	)

	useEffect(() => {
		const hasUnbookableDateSelected = () => {
			if (!startDate) return false

			if (!endDate) return false

			const dayBeforeEndDate = subDays(endDate, 1)

			const cantDepart = getDayInfo(dayBeforeEndDate)?.closedToDeparture

			if (cantDepart) return true

			const hasClosedDays = eachDayOfInterval({
				start: startDate,
				end: dayBeforeEndDate,
			}).some((date) => !getDayInfo(date) || getDayInfo(date)?.closed)

			if (hasClosedDays) return true

			return Boolean(getDayInfo(endDate)?.closed && getDayInfo(dayBeforeEndDate)?.closed)
		}

		setShowCrossSelling(hasUnbookableDateSelected())
	}, [endDate, getDayInfo, setShowCrossSelling, startDate])

	useEffect(() => {
		setIsMinNightValid(true)
		setEarliestDeparture(undefined)
		if (!startDate || !endDate) return

		const minNights = getDayInfo(startDate)?.minNights
		if (!minNights) return

		if (nights < minNights) {
			setIsMinNightValid(false)
			setEarliestDeparture({
				date: startDate,
				earliestDeparture: addDays(startDate, minNights),
				minNights,
			})
		}
	}, [endDate, getDayInfo, nights, setIsMinNightValid, startDate])

	return (
		<div>
			{earliestDeparture && <MinNightsPopup {...earliestDeparture} />}
			<div className="md:px-16 lg:flex lg:gap-x-32">
				<button
					onClick={setPreviousMonth}
					disabled={isBefore(startOfCurrentMonth, today)}
					className={cn(
						'hidden lg:block',
						isBefore(startOfCurrentMonth, today) && 'cursor-not-allowed opacity-50'
					)}
				>
					<SvgIcon iconName="chevron_left" className="h-24 w-24" />
				</button>
				<div
					className={cn(
						'flex flex-col gap-24 lg:grid lg:grow lg:grid-cols-2 lg:gap-[80px]'
					)}
				>
					<CalendarMonth
						monthName={translate(i18nMonths[getMonth(startOfCurrentMonth)])}
						daysOfMonth={daysOfFirstMonth}
						getDayInfo={getDayInfo}
						hasHoliday={hasHoliday}
						onSelect={setTravelDate}
						today={today}
						selectedDates={{ startDate, endDate }}
						onHover={setHoverDay}
						isHover={isHover}
					/>
					<div className={cn('flex justify-between lg:hidden')}>
						<button
							onClick={setPreviousMonth}
							disabled={isBefore(startOfCurrentMonth, today)}
							className={cn(
								isBefore(startOfCurrentMonth, today) &&
									'cursor-not-allowed opacity-50'
							)}
						>
							<SvgIcon iconName="chevron_left" className="h-24 w-24" />
						</button>
						<button onClick={setNextMonth}>
							<SvgIcon iconName="chevron_left" className="h-24 w-24 rotate-180" />
						</button>
					</div>
					<CalendarMonth
						daysOfPreviousMonth={daysOfFirstMonth}
						monthName={translate(i18nMonths[getMonth(startOfNextMonth)])}
						daysOfMonth={daysOfSecondMonth}
						getDayInfo={getDayInfo}
						hasHoliday={hasHoliday}
						onSelect={setTravelDate}
						today={today}
						selectedDates={{ startDate, endDate }}
						onHover={setHoverDay}
						isHover={isHover}
					/>
				</div>
				<button onClick={setNextMonth} className={cn('hidden lg:block')}>
					<SvgIcon iconName="chevron_left" className="h-24 w-24 rotate-180" />
				</button>
			</div>
		</div>
	)
}

type CalendarMonthsProps = {
	monthName: string
	daysOfMonth: Date[]
	daysOfPreviousMonth?: Date[]
	getDayInfo: (day: Date) => MappedDayInfo | undefined
	hasHoliday: (day: Date) => { publicHolidays: boolean; schoolHolidays: boolean }
	onSelect: (day: Date) => void
	today: Date
	selectedDates: {
		startDate?: Date
		endDate?: Date
	}
	onHover: React.Dispatch<React.SetStateAction<Date>>
	isHover: (day: Date) => boolean | undefined
}

export const CalendarMonth = ({
	monthName,
	daysOfMonth,
	daysOfPreviousMonth,
	today,
	getDayInfo,
	onSelect,
	hasHoliday,
	selectedDates,
	onHover,
	isHover,
}: CalendarMonthsProps) => {
	const isStartOrEndDate = (day: Date) => {
		if (selectedDates.startDate && isSameDay(day, selectedDates.startDate)) return true
		if (selectedDates.endDate && isSameDay(day, selectedDates.endDate)) return true

		if (
			selectedDates.startDate &&
			selectedDates.endDate &&
			isWithinInterval(day, {
				start: selectedDates.startDate,
				end: selectedDates.endDate,
			})
		) {
			return true
		}

		return false
	}

	return (
		<div>
			<div className={cn('text-center')}>
				{monthName} {daysOfMonth[0].getFullYear()}
			</div>
			<div className="py-s grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_1fr] pb-16 text-center">
				<div className="px-xs md:px-s pl-0">
					{translate('travel-period-days-short-monday')}
				</div>
				<div className="px-xs md:px-s">{translate('travel-period-days-short-tuesday')}</div>
				<div className="px-xs md:px-s">
					{translate('travel-period-days-short-wednesday')}
				</div>
				<div className="px-xs md:px-s">
					{translate('travel-period-days-short-thursday')}
				</div>
				<div className="px-xs md:px-s">{translate('travel-period-days-short-friday')}</div>
				<div className="px-xs md:px-s">
					{translate('travel-period-days-short-saturday')}
				</div>
				<div className="px-xs md:px-s pr-0">
					{translate('travel-period-days-short-sunday')}
				</div>
			</div>
			<div className="mt-s grid grid-cols-7 gap-2 text-sm md:gap-12 lg:gap-2 xl:gap-12">
				{daysOfMonth.map((day, dayIndex) => {
					const dayInfo = getDayInfo(day)
					const isPast = isBefore(day, today)
					const closed = Boolean(dayInfo && dayInfo.closed)
					const cantArrive = Boolean(
						(dayInfo && dayInfo.closedToArrival) || dayInfo?.closed
					)

					const previousDay =
						dayIndex === 0
							? daysOfPreviousMonth?.[daysOfPreviousMonth.length - 1]
							: daysOfMonth[dayIndex - 1]

					const cantDepart = previousDay
						? Boolean(getDayInfo(previousDay)?.closedToDeparture)
						: false

					const isDayBeforeClosed =
						previousDay && getDayInfo(previousDay)
							? Boolean(getDayInfo(previousDay)?.closed)
							: true

					return (
						<CalendarDay
							key={day.toString()}
							today={today}
							day={day}
							dayInfo={dayInfo}
							dayIndex={dayIndex}
							isPast={isPast}
							cantArrive={cantArrive}
							cantDepart={cantDepart}
							closed={closed}
							isDayBeforeClosed={isDayBeforeClosed}
							startDate={selectedDates.startDate}
							endDate={selectedDates.endDate}
							hasPromotion={Boolean(dayInfo?.promotionCode)}
							holidays={hasHoliday(day)}
							onSelect={onSelect}
							isSelected={isStartOrEndDate(day)}
							onHover={onHover}
							isHover={isHover(day)}
						/>
					)
				})}
			</div>
		</div>
	)
}

type CalendarDayProps = {
	today: Date
	day: Date
	dayInfo?: MappedDayInfo
	dayIndex: number
	isPast?: boolean
	cantArrive: boolean
	cantDepart: boolean
	closed: boolean
	isDayBeforeClosed: boolean
	hasPromotion: boolean
	holidays: {
		publicHolidays: boolean
		schoolHolidays: boolean
	}
	startDate?: Date
	endDate?: Date
	onSelect: (day: Date) => void
	onHover: React.Dispatch<React.SetStateAction<Date | undefined>>
	isHover?: boolean
	isSelected: boolean
}

export const CalendarDay = ({
	today,
	day,
	dayInfo,
	dayIndex,
	isPast = false,
	cantArrive,
	cantDepart,
	closed,
	isDayBeforeClosed,
	hasPromotion,
	startDate,
	endDate,
	holidays,
	onSelect,
	onHover,
	isHover,
	isSelected = false,
}: CalendarDayProps) => {
	const fullClosed = (!dayInfo && isDayBeforeClosed) || (closed && isDayBeforeClosed)

	const cantSelectAsStartDate = Boolean(
		(cantArrive && !startDate) ||
			(cantArrive && startDate && endDate) ||
			(cantArrive && startDate && isBefore(day, startDate)) ||
			fullClosed
	)

	const cantSelectAsEndDate = Boolean(
		(!endDate && cantDepart && startDate && isAfter(day, startDate)) || fullClosed
	)

	const cantSelect = isPast || fullClosed || cantSelectAsStartDate || cantSelectAsEndDate

	return (
		<button
			role="button"
			aria-label={format(day, 'yyyy-MM-dd')}
			data-testid={format(day, 'yyyy-MM-dd')}
			className={cn(
				'grid h-[80px] grid-rows-[8px_1fr_1fr] gap-y-2 rounded-4 border border-current p-4 text-[14px] text-textColor transition duration-150 ease-in-out md:p-8 lg:p-4 xl:p-8',
				'md:text-travelPeriod-text',
				dayIndex === 0 && colStartClasses[getDay(day)],
				(isPast || fullClosed) && 'opacity-50',
				!(isPast || fullClosed) &&
					'group hover:bg-textColor hover:text-travelPeriod-accent md:hover:bg-travelPeriod-text md:hover:text-white',
				(isSelected || isHover) &&
					'bg-textColor text-travelPeriod-accent md:bg-travelPeriod-text md:text-white',
				cantSelect && 'cursor-not-allowed'
			)}
			disabled={cantSelect}
			onMouseEnter={dayInfo && (() => onHover(day))}
			onMouseLeave={dayInfo && (() => onHover(undefined))}
			onClick={() => !isBefore(day, today) && !cantSelect && onSelect(day)}
			tabIndex={cantSelect ? -1 : undefined}
		>
			<div className={cn('grid h-8 grid-cols-5 justify-start gap-x-2')}>
				{!isPast && cantArrive && (
					<div
						data-testid="cant-arrive"
						className={cn('col-start-1 h-8 w-8 rounded-full bg-accent-1')}
					/>
				)}
				{!isPast && cantDepart && (
					<div
						data-testid="cant-depart"
						className={cn('col-start-2 h-8 w-8 rounded-full bg-accent-5')}
					/>
				)}
				{!isPast && holidays.publicHolidays && (
					<div
						data-testid="has-holidays"
						className={cn('col-start-3 h-8 w-8 rounded-full bg-accent-2')}
					/>
				)}
				{!isPast && holidays.schoolHolidays && (
					<div
						data-testid="has-school-holidays"
						className={cn('col-start-4 h-8 w-8 rounded-full bg-accent-3')}
					/>
				)}
				{!isPast && hasPromotion && (
					<div
						data-testid="has-promotion"
						className={cn('col-start-5 h-8 w-8 rounded-full bg-accent-4')}
					/>
				)}
			</div>
			<time
				className={cn(
					'border-b border-current text-current group-hover:border-current group-hover:text-current',
					fontStyles['p-bold']
				)}
			>
				{format(day, 'd')}
			</time>
			{dayInfo && !(dayInfo.closed || dayInfo.closedToArrival) ? (
				<div>
					<Typography element="p" style="p-s">
						{translate('travel-period-from-price')}
					</Typography>
					<Typography element="p" style="p-s-bold">
						{dayInfo.minPrice && formatCurrency(dayInfo.minPrice)}
					</Typography>
				</div>
			) : (
				<div>-</div>
			)}
		</button>
	)
}
